import { Button } from '@components/Button'
import { CertificateCard } from '@components/certificates/CertificateCard'
import { EmptyStateComponent } from '@components/EmptyStateComponent'
import { ICertificate } from '@dtos/Certificate'
import { AppLayout } from '@layouts/AppLayout'
import { useGetUserCertificates } from '@services/api/graphql/userCertificates'
import { useToastController } from '@tamagui/toast'
import { useFocusEffect } from 'expo-router'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { H2, View, YStack } from 'tamagui'

const PAGE_SIZE = 12

export default function Certificates() {
  const { t } = useTranslation('Certificates')
  const toast = useToastController()
  const hasLoadedOnce = useRef(false)
  const [certificates, setCertificates] = useState<ICertificate[] | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const { getUserCertificates, loading, called } = useGetUserCertificates({
    offset: 0,
    limit: PAGE_SIZE,
  })

  const fetchCertificates = useCallback(
    async (offset: number) => {
      const { data, error } = await getUserCertificates({
        variables: { offset, limit: PAGE_SIZE },
      })

      if (error) {
        const isEmptyPage =
          error.graphQLErrors?.[0]?.extensions?.code === 'empty_page' ||
          error.message === 'Empty page!'
        if (isEmptyPage) {
          setHasMore(false)
          if (offset === 0) setCertificates([])
          return
        }
        throw error
      }

      const page: ICertificate[] = data?.userCertificates || []
      setHasMore(page.length === PAGE_SIZE)
      setCertificates((prev) =>
        offset === 0 ? page : [...(prev || []), ...page],
      )
    },
    [getUserCertificates],
  )

  const loadInitial = useCallback(async () => {
    try {
      await fetchCertificates(0)
      hasLoadedOnce.current = true
    } catch (error) {
      setCertificates([])
      toast.show(t('toast.error.title'), {
        message: t('toast.error.message'),
        type: 'error',
      })
    }
  }, [fetchCertificates, toast, t])

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !certificates) return
    setIsLoadingMore(true)
    try {
      await fetchCertificates(certificates.length)
    } catch (error) {
      toast.show(t('toast.error.title'), {
        message: t('toast.error.message'),
        type: 'error',
      })
    } finally {
      setIsLoadingMore(false)
    }
  }, [isLoadingMore, certificates, fetchCertificates, toast, t])

  useFocusEffect(
    useCallback(() => {
      loadInitial()
    }, [loadInitial]),
  )

  const displayLoading =
    !hasLoadedOnce.current && (certificates === null || (loading && !called))

  const hasCertificates = certificates && certificates.length > 0

  return (
    <AppLayout backRoute={{ label: t('backToHome') }}>
      <YStack
        marginTop={122}
        paddingHorizontal="$size.spacing-xs"
        gap="$size.spacing-md"
        flex={1}
        $gtSm={{ paddingHorizontal: '$size.spacing-xsl' }}
      >
        <H2 color="$text-primary" fontSize="$font-size-md" ff="$mulishBold">
          {t('title')}
        </H2>

        {displayLoading ? null : hasCertificates ? (
          <YStack gap="$size.spacing-xs">
            {certificates.map((certificate) => (
              <CertificateCard key={certificate.id} certificate={certificate} />
            ))}

            {hasMore && (
              <View alignItems="center" marginTop="$size.spacing-xs">
                <Button
                  variant="secondary"
                  outline
                  loading={isLoadingMore}
                  onPress={handleLoadMore}
                >
                  {t('loadMore')}
                </Button>
              </View>
            )}
          </YStack>
        ) : (
          <View testID="certificates-empty-state" flex={1}>
            <EmptyStateComponent
              title={t('emptyState.title')}
              description={t('emptyState.description')}
              displayButton={false}
            />
          </View>
        )}
      </YStack>
    </AppLayout>
  )
}
